import { api } from '../lib/api'
import { Answers } from '../interviews/types'

export type RecordInterview = {
  id: number
  active: boolean
  date: string | null
  created_at: string
  session_number: number | null
  session_key: string | null
  version_key: string | null
  health_center_key: string | null
  profession_key: string | null
  profession_name: string | null
  human_session_name: string
  clinical_record_id: string
  professional: { name: string; rut: string }
  answers: Answers
  legacy_html: string | null
  editable: boolean
}

export type RecordTemplate = {
  session_key: string
  version_key: string
  health_center_key: string
  profession_key: string
  human_session_name: string
}

export type ClinicalRecordData = {
  patient: { id: string; name: string | null; sex: string | null }
  clinical_record_id: string | null
  profession: {
    key: string
    name: string
    recipe_field_labels: Record<string, string>
    recipe_type_labels: Record<string, string>
    recipe_default_option: string
    templates: RecordTemplate[]
  }
  health_center: {
    id: number
    days_for_modification: number
    account_state: 'open' | 'soon' | 'closed'
    trial_features_enabled: boolean
  }
  has_attention_permission: boolean
  interviews: RecordInterview[]
}

export function fetchClinicalRecord(
  healthCenterId: number,
  patientId: string,
  recordId: string,
  showInactive: boolean,
): Promise<ClinicalRecordData> {
  return api.get<ClinicalRecordData>(
    `/api/health_centers/${healthCenterId}/patients/${patientId}/clinical_records/${recordId}?show_inactive=${showInactive}`,
  )
}

export type InterviewFormData = {
  interview: { id: number; date: string | null; session_number: number | null; answers: Answers } | null
  template: RecordTemplate | null
  clinical_record_id: string
  patient_id: string
  recipe_field_labels: Record<string, string>
  recipe_type_labels: Record<string, string>
  recipe_default_option: string
  previous_complete_id: number | null
  previous_id: number | null
  patients_count: number
  new_interview_number?: number
  readonly?: boolean
}

export function fetchInterviewForm(
  healthCenterId: number,
  options: { id?: string; clinicalRecordId: string; templateSessionKey?: string },
): Promise<InterviewFormData> {
  const params = new URLSearchParams()
  params.set('clinical_record_id', options.clinicalRecordId)
  if (options.templateSessionKey) {
    params.set('template_session_key', options.templateSessionKey)
  }
  const base = options.id
    ? `/health_centers/${healthCenterId}/interviews/${options.id}/edit.json`
    : `/health_centers/${healthCenterId}/interviews/new.json`
  return api.get<InterviewFormData>(`${base}?${params.toString()}`)
}

export type InterviewSaveResult = { id: number; notice: string; redirect: string }

export function createInterview(
  healthCenterId: number,
  payload: { date: string; clinical_record_id: string; session_key: string; version_key: string; answers: Answers },
): Promise<InterviewSaveResult> {
  return api.post<InterviewSaveResult>(`/health_centers/${healthCenterId}/interviews.json`, { interview: payload })
}

export function updateInterview(
  healthCenterId: number,
  id: number,
  payload: { date: string; clinical_record_id: string; session_key: string; version_key: string; answers: Answers },
): Promise<InterviewSaveResult> {
  return api.patch<InterviewSaveResult>(`/health_centers/${healthCenterId}/interviews/${id}.json`, { interview: payload })
}

export function deleteInterview(healthCenterId: number, id: number, clinicalRecordId: string): Promise<void> {
  return api.delete(`/health_centers/${healthCenterId}/interviews/${id}.json?clinical_record_id=${clinicalRecordId}`)
}

export function toggleInterviewActive(healthCenterId: number, id: number, clinicalRecordId: string): Promise<{ active: boolean; notice: string }> {
  return api.post(`/health_centers/${healthCenterId}/interviews/${id}/toggles_active.json?clinical_record_id=${clinicalRecordId}`)
}
